/* eslint-disable no-unsafe-optional-chaining */
import { message } from 'antd';
import { AxiosError } from 'axios';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery } from 'react-query';

import { useAppSelector } from 'app/store';
import { DropdownElement, DropdownElementProps } from 'common/components/DropdownType';
import { getAllStaffFulltimeService } from 'common/services/extends/staffFulltime';
import { DepartmentEnum, OrderByEnum, OrderTypeEnum } from 'common/services/extends/staffFulltime/types';
import { detectError } from 'common/utils/functions';
import roles, { getPermission } from 'configs/roles';

interface DropdownPayrollProps extends DropdownElementProps {
  exceptIds?: string

}
const DropdownPayroll: React.FC<DropdownPayrollProps> = ({ exceptIds, ...props }) => {
  const { t } = useTranslation();
  const [searchPayroll, setSearchPayroll] = React.useState('');
  const rolesUser = useAppSelector((state) => state.auth.roles);
  const {
    data: payrollsRes, isLoading: payrollsLoading, isFetching, fetchNextPage, hasNextPage
  } = useInfiniteQuery(
    ['get-payrolls', exceptIds, searchPayroll],
    ({ pageParam }) => getAllStaffFulltimeService({
      page: pageParam || 1,
      _department: DepartmentEnum.sup,
      _orderBy: OrderByEnum.fullname,
      _orderType: OrderTypeEnum.asc,
      ...exceptIds && { _exceptStaffIds: exceptIds },
      ...searchPayroll && { keyword: searchPayroll }
    }),
    {
      enabled: getPermission(rolesUser, roles.EMPLOYEE_FULLTIME_INDEX),
      getNextPageParam: (lastPage) => (lastPage?.meta?.page >= lastPage?.meta?.totalPages
        ? undefined : lastPage?.meta?.page + 1),
      onError: (error: unknown) => {
        if (error instanceof AxiosError) {
          const status = error.response?.status;
          message.error(t(detectError(status)));
        }
      }
    }
  );

  const formatPayrolls: OptionType[] = useMemo(() => payrollsRes?.pages.reduce((acc, page) => {
    const newAcc = [...acc, ...page.data.map((item) => ({
      label: item.staffFullTimeData.fullname,
      value: item.staffFullTimeData.id
    }))];
    return newAcc;
  }, [] as OptionType[])
    || [], [payrollsRes]);

  const handleLoadMore = () => {
    if (hasNextPage) fetchNextPage();
  };

  return (
    <div className="dropdownPayroll">
      <DropdownElement
        options={formatPayrolls}
        loading={payrollsLoading || isFetching}
        handleLoadMore={handleLoadMore}
        handleExternalSearch={(value) => setSearchPayroll(value)}
        {...props}
      />
    </div>
  );
};

export default DropdownPayroll;
